import { useEffect, useRef } from 'react';
import type { UserPreferences } from '@my-fullstack-app/itinerary-types';
import { trpc } from '../lib/trpc';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  INITIAL_THEME_SYNC_STATE,
  stepThemeSync,
  type ThemeSyncState,
} from '../lib/theme-sync';

/**
 * Bridges the local-only theme preference (ThemeContext → AsyncStorage)
 * to the server-backed `UserPreferences.themeMode` field so the choice
 * follows the user across devices.
 *
 * ThemeContext remains the source of truth for first paint — it hydrates
 * from a plain AsyncStorage key before the provider tree (incl. tRPC)
 * mounts, which is what prevents the light→dark flash on launch
 * (ADR-0033). This bridge sits below `trpc.Provider` and reconciles that
 * local value with the server once both are available:
 *
 *   - Inbound (server wins on load): a signed-in user's saved themeMode
 *     is adopted locally — pick Dark on the phone, see Dark on the laptop
 *     on its next load. Cross-device changes land on the next mount, same
 *     as the other display prefs (see profile.tsx).
 *   - Outbound: a theme toggle, or an existing local choice when the
 *     server has none yet, is pushed up.
 *
 * The reconciliation decision lives in the pure `stepThemeSync` reducer;
 * this component just feeds it the live local/server values and applies
 * the resulting action. Render once inside the trpc.Provider tree.
 */
export function ThemePrefSync() {
  const { isAuthenticated } = useAuth();
  const { mode, setMode, hydrated } = useTheme();
  const utils = trpc.useUtils();

  const prefsQuery = trpc.userPrefs.get.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  // `mutate` is referentially stable across renders (React Query), so it's
  // safe in the effect deps without re-triggering on every mutation state
  // change.
  const { mutate: pushPrefs } = trpc.userPrefs.update.useMutation({
    // Mirror the merged record into the shared cache so usePreferences /
    // the profile screen pick up the new themeMode without a refetch.
    onSuccess: (data) => utils.userPrefs.get.setData(undefined, data),
  });

  const serverMode = (prefsQuery.data as UserPreferences | undefined)?.themeMode;
  const stateRef = useRef<ThemeSyncState>(INITIAL_THEME_SYNC_STATE);

  // Reset the per-session reconcile flag on sign-out so a subsequent
  // sign-in (possibly as a different user) re-adopts the server value
  // instead of treating its own stale local mode as authoritative.
  useEffect(() => {
    if (!isAuthenticated) stateRef.current = INITIAL_THEME_SYNC_STATE;
  }, [isAuthenticated]);

  useEffect(() => {
    // Wait until the local theme has hydrated (so we don't race the
    // AsyncStorage read) and the server record has settled (so we know
    // its value before deciding which way to write).
    if (!isAuthenticated || !hydrated || !prefsQuery.isSuccess) return;

    const step = stepThemeSync(stateRef.current, serverMode, mode);
    stateRef.current = step.state;
    if (step.applyLocal) setMode(step.applyLocal);
    if (step.pushServer) pushPrefs({ themeMode: step.pushServer });
  }, [isAuthenticated, hydrated, prefsQuery.isSuccess, serverMode, mode, setMode, pushPrefs]);

  return null;
}
